<template>
  <section class="section-confirm-professional-booking p-4">
    <!-- Navigation -->
    <div class="row mb-3">
      <div class="col-12 col-md-8 offset-md-2">
        <router-link :to="(isLineLoggedIn) ? '/line-liff/bookings' : '/my-reservations'" class="text-primary-link">{{ $t('message.menu.my_reservations') }}</router-link>
        <i class="fal fa-angle-right"></i>
        <router-link class="text-primary-link" :to="`/reservation/${order.id}`">
          {{ $t('message.my_reservations.reservation_detail') }}
        </router-link>
        <i class="fal fa-angle-right"></i>
        {{ $t('message.my_reservations.payment') }}
      </div>
    </div>
    <div class="row">
      <div class="col-12 col-lg-6 offset-lg-3">
        <!-- Section Amount -->
        <div class="card shadow-sm">
          <div class="card-body">
            <div v-if="order.sender_type === 'professional'">
              <checkout-cost :hasDivider="false" @onChangedCostData="updateCostData"/>
            </div>
            <div v-else>
              <order-summary-total-short :order="order"/>
            </div>
          </div>
        </div>
        <!-- Payment -->
        <section class="pt-3" v-if="costData && costData.totalPrice">
          <payment-methods ref="paymentMethods"
                           @onSelectedPaymentMethod="updatePaymentMethod"
                           @onSelectedCreditCard="applyUserCreditCard"
                           @onRequestedSavingTempData="saveTempData"
                           @onSelectedFileUpload="setFileUpload"
                           @onRequestedUpdatingQrResponse="updateQrResponse"
                           @onSuccessQrPayment="displaySuccessDialog"></payment-methods>
        </section>

        <div class="pt-2">
          <button class="btn btn-info btn-block"
                  :disabled="isLoading"
                  @click="formValidation()">
            {{ isLoading ? $t('message.button.loading') : $t('message.button.make_a_payment') }}
          </button>
        </div>
      </div>
    </div>
    <snackbar ref="snackbar"
              baseSize="100px"
              :colors="{open: '#333', info: '#3DBD7D', error: '#FA7377', warn: '#FF6600'}"
              :holdTime="2500"
              :position="'bottom-center'"/>
    <!-- Back navigation for line liff -->
    <div class="container text-center pt-3 pb-4" v-if="isLineLoggedIn">
      <router-link :to="`/reservation/${order.id}`">
        <i class="fas fa-angle-left"></i> {{ $t('message.booking.new_booking.back') }}
      </router-link>
    </div>
  </section>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import { orderMixin } from '../../mixins/orderMixin'
  import PaymentMethods from './PaymentMethods'
  import InviteFriendModal from '../booking/components/InviteFriendModal'
  import MyPromoCodeModal from '../booking/components/MyPromoCodeModal'
  import CheckoutCost from '../booking/components/CheckoutCost'
  import axios from 'axios'
  import OrderSummaryTotalShort from './components/OrderSummaryTotalShort'

  export default {
    mixins: [orderMixin],
    components: {
      OrderSummaryTotalShort,
      CheckoutCost,
      PaymentMethods,
      InviteFriendModal,
      MyPromoCodeModal
    },
    data () {
      return {
        paymentMethod: null,
        omiseCustomerId: null,
        fileUpload: null,
        isLoading: false,
        costData: null,
        displaySubmitButton: true
      }
    },
    computed: {
      ...mapGetters({
        appliedPromoCode: 'promoCode/getAppliedPromoCode',
        globalState: 'globalVal/getState',
        order: 'orderDetail/getOrderDetail',
        user: 'auth/getUser'
      }),
      ...mapState('auth', ['isLineLoggedIn']),
      ...mapState('booking', ['preBookingData', 'preCheckoutData', 'tmpBookingData', 'tmpPreCheckoutData'])
    },
    watch: {
      'costData.totalPrice': function (newVal, oldVal) {
        if (newVal === 0) {
          this.paymentMethod = 'bank_transfer'
          this.$refs.paymentMethods.setPaymentMethod(this.paymentMethod)
        }
      },
      paymentMethod () {
        this.updateQrPayment()
      },
      preCheckoutData () {
        if (this.preCheckoutData && this.order) {
          const data = {
            totalPrice: this.order.total_price
          }
          this.$store.commit('booking/SET_BOOKING_COST', data)
          this.calculateServiceCost()
        }
      },
      appliedPromoCode () {
        if (this.appliedPromoCode) {
          if (this.appliedPromoCode.is_applied) {
            this.displaySnackbar(this.$i18n.t('message.promo_code.you_applied_promo_code'))
          } else if (this.appliedPromoCode.promo_code.payment_method && !this.appliedPromoCode.is_applied && this.appliedPromoCode.status === 'not_matched_payment_method') {
            this.displaySnackbar(this.$i18n.t('message.promo_code.not_matched_payment_method'))
          }
        }
      }
    },
    methods: {
      ...mapActions('orderDetail', ['loadOrderDetail']),
      updateCostData (costData) {
        this.costData = costData
        this.updateQrPayment()
      },
      displaySnackbar (message) {
        this.$refs.snackbar.open(message)
      },
      loadPreCheckout () {
        const submitData = {
          user_id: this.user.id,
          province_id: this.order.province_id,
          user_place_id: this.order.user_place_id,
          professional_id: this.order.professional_id,
          work_hour: this.order.work_hour,
          service_id: this.order.service_id
        }
        this.$store.dispatch('booking/LOAD_PRE_CHECKOUT_DATA', submitData)
      },
      initData () {
        if (this.order) {
          // Set cost data case booked by user
          if (this.order.sender_type === 'user') {
            this.costData = {
              totalPrice: this.order.total_price,
              discountCost: this.order.discount_cost,
              creditDiscountCost: this.order.credit_discount_cost
            }
          }

          // Set preBookingData
          const preBookingData = {
            service_id: this.order.service_id,
            province_id: this.order.province_id,
            work_hour: this.order.work_hour,
            init_work_hour: this.order.work_hour
          }
          this.$store.commit('booking/SET_PRE_BOOKING_DATA', preBookingData)

          // Check promo code to auto apply
          if (this.tmpBookingData && this.tmpBookingData.applied_promo_code) {
            this.$store.commit('promoCode/SET_APPLIED_PROMO_CODE', this.tmpBookingData.applied_promo_code)
            this.autoApplyPromoCode()
          }

          // Set preCheckoutData
          if (this.tmpPreCheckoutData) {
            this.$store.commit('booking/SET_PRE_CHECKOUT_DATA', this.tmpPreCheckoutData)
            // Check applied coins
            if (this.tmpPreCheckoutData.is_credit && Number(this.tmpPreCheckoutData.total_credits) <= 0) {
              this.$store.commit('booking/UPDATE_PRE_CHECKOUT_DATA', {key: 'is_credit', value: false})
            }
            // Update payment method
            if (this.tmpPreCheckoutData.payment_method) {
              setTimeout(() => {
                this.paymentMethod = this.tmpPreCheckoutData.payment_method
                this.$refs.paymentMethods.setPaymentMethod(this.paymentMethod)
                this.omiseCustomerId = this.tmpPreCheckoutData.omise_customer_id
              }, 500)
            }
          } else {
            this.loadPreCheckout()
          }
        }
      },
      autoApplyPromoCode () {
        if (this.appliedPromoCode) {
          const submitData = {
            user_id: this.user.id,
            promo_code: this.appliedPromoCode.promo_code.code,
            province_id: this.preBookingData.province_id,
            service_id: this.preBookingData.service_id,
            work_hour: this.preBookingData.work_hour,
            payment_method: this.paymentMethod,
            omise_customer_id: this.omiseCustomerId
          }
          this.$store.dispatch('promoCode/APPLY_PROMO_CODE', submitData)
        }
      },
      updateQrPayment () {
        if (this.paymentMethod === 'promptpay' && this.costData) {
          this.$refs.paymentMethods.setAllowLoadQrCode(false)
        }
      },
      updatePaymentMethod (paymentMethod) {
        this.paymentMethod = paymentMethod
        if (paymentMethod !== 'credit_card') {
          this.omiseCustomerId = null
          this.$store.commit('booking/UPDATE_PRE_CHECKOUT_DATA', {key: 'payment_method', value: this.paymentMethod})
          this.$store.commit('booking/UPDATE_PRE_CHECKOUT_DATA', {
            key: 'omise_customer_id',
            value: this.omiseCustomerId
          })
          this.autoApplyPromoCode()
        }
      },
      applyUserCreditCard (data) {
        this.paymentMethod = 'credit_card'
        this.omiseCustomerId = data.omise_customer_id
        this.$store.commit('booking/UPDATE_PRE_CHECKOUT_DATA', {key: 'payment_method', value: this.paymentMethod})
        this.$store.commit('booking/UPDATE_PRE_CHECKOUT_DATA', {key: 'omise_customer_id', value: this.omiseCustomerId})
        this.autoApplyPromoCode()
      },
      saveTempData () {
        if (this.appliedPromoCode) {
          this.$store.commit('booking/SET_TMP_BOOKING_DATA', {applied_promo_code: this.appliedPromoCode})
        }
        this.$store.commit('booking/SET_TMP_PRE_CHECKOUT_DATA', this.preCheckoutData)
      },
      setFileUpload (file) {
        this.fileUpload = file
      },
      updateQrResponse (qrResponse) {
        console.log('check qr response', qrResponse)
      },
      displaySuccessDialog (alertMessage = this.$i18n.t('message.payment_method.payment_success')) {
        // Clear temp data
        this.$store.commit('booking/SET_TMP_BOOKING_DATA', null)
        this.$store.commit('booking/SET_TMP_PRE_CHECKOUT_DATA', null)
        this.$store.commit('booking/SET_PRE_CHECKOUT_DATA', null)
        this.$store.commit('promoCode/SET_APPLIED_PROMO_CODE', null)
        this.$swal({
          text: alertMessage,
          type: 'success',
          allowOutsideClick: false,
          showCancelButton: false,
          confirmButtonColor: '#17a2b8',
          confirmButtonText: this.$i18n.t('message.button.close')
        }).then((result) => {
          if (result) {
            this.$router.push(`/reservation/${this.order.id}`)
          }
        })
      },
      formValidation () {
        if (this.paymentMethod) {
          switch (this.paymentMethod) {
            case 'credit_card':
              if (!this.omiseCustomerId) {
                this.$swal({text: this.$i18n.t('message.alert.select_credit_card')})
              } else {
                this.confirmOrderPayment()
              }
              break
            case 'bank_transfer':
              if (!this.fileUpload) {
                const alertMessage = this.$i18n.t('message.alert.no_file_upload')
                this.$swal({text: alertMessage, type: 'error'})
              } else {
                this.confirmOrderPayment()
              }
              break
            case 'promptpay':
              if (this.order.sender_type === 'professional') {
                this.$swal({
                  title: this.$i18n.t('message.confirm.apply_discount_before_creating_qr_code_title'),
                  text: this.$i18n.t('message.confirm.apply_discount_before_creating_qr_code_detail'),
                  showCancelButton: true,
                  type: 'warning',
                  confirmButtonColor: '#17a2b8',
                  cancelButtonColor: '#9E9E9E',
                  reverseButtons: true,
                  confirmButtonText: this.$i18n.t('message.button.yes'),
                  cancelButtonText: this.$i18n.t('message.button.cancel')
                }).then(() => {
                  this.confirmOrderPayment()
                })
              } else {
                this.confirmOrderPayment()
              }
              break
          }
        } else {
          this.$swal({text: this.$i18n.t('message.alert.select_payment_method')})
        }
      },
      performLoadQrCode () {
        const qrPaymentData = {
          paymentFor: 'normal',
          orderId: this.order.id,
          amount: Number(this.costData.totalPrice)
        }
        this.$refs.paymentMethods.setAllowLoadQrCode(true)
        this.$refs.paymentMethods.updateQrPaymentData(qrPaymentData)
      },
      async confirmOrderPayment () {
        this.isLoading = true
        let submitData = null
        if (this.paymentMethod === 'bank_transfer') {
          let formData = new FormData()
          formData.append('user_id', this.user.id)
          formData.append('payment_method', this.paymentMethod)
          formData.append('upload_image', this.fileUpload)
          if (this.appliedPromoCode && this.appliedPromoCode.is_applied) {
            formData.append('promo_code_id', this.appliedPromoCode.id)
          }
          if (this.costData && this.costData.creditDiscountCost) {
            formData.append('credit_discount_cost', this.costData.creditDiscountCost)
            formData.append('is_credit', '1')
          }
          formData.append('is_new_cost_format', '1')
          submitData = formData
        } else {
          submitData = {
            user_id: this.user.id,
            payment_method: this.paymentMethod,
            omise_customer_id: this.omiseCustomerId,
            promo_code_id: (this.appliedPromoCode && this.appliedPromoCode.is_applied) ? this.appliedPromoCode.id : null,
            credit_discount_cost: (this.costData && this.costData.creditDiscountCost) ? this.costData.creditDiscountCost : null,
            is_credit: (this.costData && this.costData.creditDiscountCost) ? 1 : 0,
            is_new_cost_format: 1
          }
        }
        const url = `/orders/${this.order.id}/confirm-professional-booking`
        const {data} = await axios.post(url, submitData)
        this.isLoading = false
        if (!data.error) {
          switch (this.paymentMethod) {
            case 'credit_card':
              this.displaySuccessDialog()
              break
            case 'bank_transfer':
              this.displaySuccessDialog(this.$i18n.t('message.payment_method.bank_transfer_success'))
              break
            case 'promptpay':
              this.$router.replace({path: `/orders/${this.order.id}/confirm-promptpay`})
              // this.performLoadQrCode()
              break
          }
        } else {
          this.$swal({text: data.message, type: 'error'})
        }
      }
    },
    async mounted () {
      this.$store.commit('promoCode/SET_APPLIED_PROMO_CODE', null)
      this.initData()
      this.$refs.paymentMethods.setAllowLoadQrCode(false)
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/order/ConfirmProfessionalBooking.vue